import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Brain, DollarSign, Loader2, User, Calendar, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AIHistoryPage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ user_email: "", prompt_key: "" });

  const load = () => {
    const params = new URLSearchParams();
    if (filter.user_email) params.set("user_email", filter.user_email);
    if (filter.prompt_key) params.set("prompt_key", filter.prompt_key);
    params.set("limit", "200");
    axios.get(`${API}/licitaciones/ai-analysis/history?${params}`)
      .then(r => setData(r.data))
      .catch(e => toast.error("Error cargando histórico: " + (e.response?.data?.detail || e.message)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.user_email, filter.prompt_key]);

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500 p-6"><Loader2 className="animate-spin" size={14}/> Cargando histórico Pando…</div>;

  const fmtEur = (v) => `$${(v || 0).toFixed(3)}`;
  const fmtTok = (n) => (n || 0).toLocaleString("es-ES");

  return (
    <div className="w-full space-y-5 p-6" data-testid="ai-history-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit'] flex items-center gap-2"><Brain size={22} className="text-purple-600"/> Histórico de Análisis Pando</h1>
          <p className="text-sm text-gray-500 mt-0.5">Trazabilidad completa de llamadas a Claude: usuarios, prompts, tokens y coste estimado.</p>
        </div>
        {data.is_admin_view && <span className="bg-purple-100 text-purple-700 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wide">Vista admin · todos los usuarios</span>}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Sparkles}  label="Llamadas Pando"    value={data.totals.calls}                        color="bg-purple-50 text-purple-700"/>
        <Kpi icon={FileText}  label="Tokens entrada" value={fmtTok(data.totals.tokens_in)}            color="bg-blue-50 text-blue-700"/>
        <Kpi icon={FileText}  label="Tokens salida"  value={fmtTok(data.totals.tokens_out)}           color="bg-green-50 text-green-700"/>
        <Kpi icon={DollarSign} label="Coste total (USD)" value={fmtEur(data.totals.cost_usd)}        color="bg-amber-50 text-amber-700"/>
      </div>

      {/* Breakdown by user */}
      {Object.keys(data.by_user).length > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="text-xs font-bold text-gray-600 uppercase tracking-wide flex items-center gap-1"><User size={11}/> Desglose por usuario</h2>
          </div>
          <table className="w-full text-xs">
            <thead className="text-gray-500 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left p-3">Usuario</th>
                <th className="text-right p-3">Llamadas</th>
                <th className="text-right p-3">Tokens IN</th>
                <th className="text-right p-3">Tokens OUT</th>
                <th className="text-right p-3">Coste (USD)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Object.entries(data.by_user).sort((a,b)=> b[1].cost_usd - a[1].cost_usd).map(([email, u]) => (
                <tr key={email} className="hover:bg-gray-50 cursor-pointer" onClick={() => setFilter({ ...filter, user_email: email })}>
                  <td className="p-3"><div className="font-semibold">{u.name}</div><div className="text-[10px] text-gray-400">{email}</div></td>
                  <td className="p-3 text-right font-mono">{u.calls}</td>
                  <td className="p-3 text-right font-mono">{fmtTok(u.tokens_in)}</td>
                  <td className="p-3 text-right font-mono">{fmtTok(u.tokens_out)}</td>
                  <td className="p-3 text-right font-mono font-bold text-amber-700">{fmtEur(u.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Filters */}
      {filter.user_email && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 flex items-center justify-between">
          <span className="text-xs text-purple-700">Filtrando por usuario: <strong>{filter.user_email}</strong></span>
          <button onClick={() => setFilter({ ...filter, user_email: "" })} className="text-xs text-purple-600 hover:text-purple-800 underline">limpiar</button>
        </div>
      )}

      {/* Items list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left p-3">Fecha</th>
                <th className="text-left p-3">Usuario</th>
                <th className="text-left p-3">Licitación</th>
                <th className="text-left p-3">Tipo de análisis</th>
                <th className="text-right p-3">Tokens IN</th>
                <th className="text-right p-3">Tokens OUT</th>
                <th className="text-right p-3">Coste</th>
                <th className="text-right p-3">Chars</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.items.map(i => (
                <tr key={i.id} className="hover:bg-gray-50" data-testid={`ai-log-${i.id}`}>
                  <td className="p-3 text-gray-500"><Calendar size={10} className="inline mr-1"/>{(i.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3"><div className="font-semibold">{i.user_name || i.user_email}</div><div className="text-[10px] text-gray-400">{i.user_role}</div></td>
                  <td className="p-3 max-w-[240px]"><div className="font-medium truncate">{i.licitacion_titulo}</div><div className="text-[10px] text-gray-400">{i.licitacion_municipio}</div></td>
                  <td className="p-3"><span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-[10px] font-semibold">{i.prompt_title}</span></td>
                  <td className="p-3 text-right font-mono text-gray-600">{fmtTok(i.input_tokens)}</td>
                  <td className="p-3 text-right font-mono text-gray-600">{fmtTok(i.output_tokens)}</td>
                  <td className="p-3 text-right font-mono font-bold text-amber-700">{fmtEur(i.cost_usd)}</td>
                  <td className="p-3 text-right font-mono text-gray-400">{fmtTok(i.length_chars)}</td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan="8" className="p-6 text-center text-gray-400 text-xs">— Sin llamadas Pando registradas —</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className={`rounded-xl p-4 ${color}`}>
      <div className="flex items-center gap-2"><Icon size={12}/><span className="text-[10px] uppercase font-bold tracking-wide opacity-70">{label}</span></div>
      <div className="text-2xl font-bold mt-1 font-mono">{value}</div>
    </div>
  );
}
